/* import __COLOCATED_TEMPLATE__ from './step-wrapper.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import { type SafeString } from '@ember/template/-private/handlebars';
import type ImportSource from 'embercom/models/import-service/import-source';
import type SalesforcePlatform from 'embercom/objects/standalone/platforms/salesforce';

interface Args {
  step?: number;
  showSideDrawer?: boolean;
  toggleSideDrawer: () => void;
  setStep: (step: number) => void;
  errorCode?: string;
  salesforceSyncSourceId?: number;
  importSource?: ImportSource;
}

export default class StandaloneSalesforceArticleSyncStepWrapper extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;

  @tracked importSource?: ImportSource;

  constructor(owner: any, args: Args) {
    super(owner, args);

    this.handleErrorCode();
  }

  get steps(): {
    title: string;
    description: string | SafeString;
    component:
      | 'standalone/salesforce/setup/article-sync/steps/connect'
      | 'standalone/salesforce/setup/article-sync/steps/filter'
      | 'standalone/salesforce/setup/article-sync/steps/map';
  }[] {
    return [
      {
        title: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.title',
        ),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.description',
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/connect',
      },
      {
        title: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.filter.title',
        ),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.filter.description',
          {
            instanceUrl: this.instanceUrl,
            htmlSafe: true,
          },
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/filter',
      },
      {
        title: this.intl.t('standalone.salesforce.setup.article-sync.step-wrapper.steps.map.title'),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.map.description',
          {
            audienceUrl: this.router.urlFor(
              this.appService.app.canUseMultiBrandGA
                ? 'apps.app.automation.fin-ai-agent.settings.audiences'
                : 'apps.app.settings.data.audiences',
            ),
            htmlSafe: true,
          },
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/map',
      },
    ];
  }

  get salesforceConfig(): SalesforceConfiguration | void {
    if (this.salesforcePlatform) {
      return this.salesforcePlatform.salesforceConfig;
    }
  }

  get salesforceImportOnlyConfig() {
    return this.salesforcePlatform?.salesforceImportOnlyConfig;
  }

  get salesforcePlatform(): SalesforcePlatform | void {
    if (this.appService.app.isStandaloneApp) {
      return this.finStandaloneService.salesforcePlatform;
    }
  }

  get currentStep() {
    return Number(this.args.step || 0);
  }

  get instanceUrl() {
    if (this.salesforceImportOnlyConfig) {
      return this.salesforceImportOnlyConfig.instanceUrl;
    }

    return this.salesforceConfig?.instanceUrl;
  }

  @action nextStep() {
    if (this.currentStep >= this.steps.length - 1) {
      this.args.toggleSideDrawer();
      return;
    }

    if (this.currentStep < this.steps.length - 1) {
      this.args.setStep(this.currentStep + 1);
    }
  }

  @action previousStep() {
    if (this.currentStep > 0) {
      this.args.setStep(this.currentStep - 1);
    }
  }

  @action setImportSource(importSource: ImportSource) {
    this.importSource = importSource;
  }

  get currentStepData() {
    return this.steps[this.currentStep];
  }

  private handleErrorCode() {
    if (!this.args.errorCode) {
      return;
    }
    this.notificationsService.notifyError(this.errorMessage);
  }

  private get errorMessage() {
    switch (this.args.errorCode) {
      case 'OAUTH_APP_BLOCKED':
        return this.intl.t(
          'standalone.salesforce.setup.manage-connection.errors.oauth-app-blocked',
          { htmlSafe: true },
        );
      case 'API_CURRENTLY_DISABLED':
      case 'API_DISABLED_FOR_ORG':
        return this.intl.t('standalone.salesforce.setup.manage-connection.errors.api-disabled');
      case 'unauthorized_client':
      case 'invalid_client':
        return this.intl.t('standalone.salesforce.setup.manage-connection.errors.bad-client');
      default:
        return this.intl.t('standalone.salesforce.setup.manage-connection.errors.generic');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::StepWrapper': typeof StandaloneSalesforceArticleSyncStepWrapper;
    'standalone/salesforce/setup/article-sync/step-wrapper': typeof StandaloneSalesforceArticleSyncStepWrapper;
  }
}
